/**
 * Identity-free spatial objects for canonical work records.
 *
 * A queue entry or approval is not a human player and is not an AI-agent
 * identity. Representing either with a character rig makes presence and roster
 * semantics untrustworthy, so OPS uses these ticket/plinth objects instead.
 */
import * as pc from 'playcanvas'
import { createRenderEntity } from './interiorShared'

export type WorkObjectKind = 'queue' | 'approval'

export interface WorkObjectMaterials {
  frame: pc.StandardMaterial
  queue: pc.StandardMaterial
  approval: pc.StandardMaterial
  signal: pc.StandardMaterial
}

export interface WorkObjectHandle {
  entity: pc.Entity
  signal: pc.Entity
  destroy(): void
}

export interface WorkObjectPartLayout {
  suffix: string
  type: 'box' | 'cylinder'
  position: readonly [number, number, number]
  scale: readonly [number, number, number]
  material: 'frame' | 'face' | 'signal'
  signal?: true
}

/**
 * Pure visual grammar for spatial work records.
 *
 * Approval objects are deliberately low desk kiosks. They have no stem,
 * torso-height card, head, or limb-like notches and therefore cannot be read
 * as static people when several approvals populate the incident room.
 */
export function workObjectPartLayout(kind: WorkObjectKind): readonly WorkObjectPartLayout[] {
  if (kind === 'approval') {
    return [
      {
        suffix: 'base',
        type: 'cylinder',
        position: [0, 0.06, 0],
        scale: [0.82, 0.12, 0.82],
        material: 'frame',
      },
      {
        suffix: 'console',
        type: 'box',
        position: [0, 0.25, 0],
        scale: [0.74, 0.32, 0.52],
        material: 'frame',
      },
      {
        suffix: 'screen',
        type: 'box',
        position: [0, 0.48, -0.08],
        scale: [0.64, 0.30, 0.08],
        material: 'face',
      },
      {
        suffix: 'signal',
        type: 'box',
        position: [0, 0.69, -0.08],
        scale: [0.54, 0.08, 0.10],
        material: 'face',
        signal: true,
      },
    ]
  }
  return [
    {
      suffix: 'base',
      type: 'cylinder',
      position: [0, 0.07, 0],
      scale: [0.72, 0.14, 0.72],
      material: 'frame',
    },
    {
      suffix: 'stem',
      type: 'box',
      position: [0, 0.48, 0],
      scale: [0.12, 0.72, 0.12],
      material: 'frame',
    },
    {
      suffix: 'ticket',
      type: 'box',
      position: [0, 0.82, 0],
      scale: [0.72, 0.78, 0.12],
      material: 'face',
    },
    {
      suffix: 'notch-a',
      type: 'box',
      position: [-0.25, 0.82, 0.08],
      scale: [0.10, 0.38, 0.04],
      material: 'frame',
    },
    {
      suffix: 'notch-b',
      type: 'box',
      position: [0.25, 0.82, 0.08],
      scale: [0.10, 0.38, 0.04],
      material: 'frame',
    },
    {
      suffix: 'signal',
      type: 'box',
      position: [0, 1.34, 0],
      scale: [0.28, 0.08, 0.08],
      material: 'signal',
      signal: true,
    },
  ]
}

function material(
  diffuse: readonly [number, number, number],
  emissive?: readonly [number, number, number],
  intensity = 1,
): pc.StandardMaterial {
  const value = new pc.StandardMaterial()
  value.diffuse = new pc.Color(...diffuse)
  value.useMetalness = true
  value.metalness = emissive ? 0.45 : 0.7
  value.gloss = emissive ? 0.72 : 0.45
  if (emissive) {
    value.emissive = new pc.Color(...emissive)
    value.emissiveIntensity = intensity
  }
  value.update()
  return value
}

export function createWorkObjectMaterials(): WorkObjectMaterials {
  return {
    frame: material([0.08, 0.10, 0.14]),
    queue: material([0.22, 0.30, 0.42], [0.38, 0.64, 0.92], 1.2),
    approval: material([0.39, 0.25, 0.10], [0.95, 0.62, 0.18], 1.4),
    signal: material([0.56, 0.66, 0.78], [0.76, 0.88, 1.0], 1.8),
  }
}

function part(
  parent: pc.Entity,
  name: string,
  type: 'box' | 'cylinder',
  position: readonly [number, number, number],
  scale: readonly [number, number, number],
  material: pc.Material,
): pc.Entity {
  const entity = createRenderEntity({ parent, name, type, position, scale, material })
  if (entity.render) entity.render.castShadows = false
  return entity
}

/**
 * A waist-high dispatch ticket on a grounded plinth. Its rectangular card,
 * base and floating signal bar deliberately avoid a head/torso/limb silhouette.
 */
export function createWorkObject(
  parent: pc.Entity,
  materials: WorkObjectMaterials,
  name: string,
  kind: WorkObjectKind,
  position: readonly [number, number, number],
  yaw: number,
): WorkObjectHandle {
  const root = new pc.Entity(name)
  parent.addChild(root)
  root.setLocalPosition(...position)
  root.setLocalEulerAngles(0, yaw, 0)

  const face = kind === 'approval' ? materials.approval : materials.queue
  let signal: pc.Entity | null = null
  for (const layout of workObjectPartLayout(kind)) {
    const materialValue = layout.material === 'frame'
      ? materials.frame
      : layout.material === 'signal'
        ? materials.signal
        : face
    const entity = part(
      root,
      `${name}-${layout.suffix}`,
      layout.type,
      layout.position,
      layout.scale,
      materialValue,
    )
    if (layout.signal) signal = entity
  }
  if (!signal) throw new Error(`work object ${kind} is missing its signal part`)

  return {
    entity: root,
    signal,
    destroy: () => root.destroy(),
  }
}
